import { isCodeFieldKey } from "./codePicks";
import { resolve, type FieldDef } from "./logTabs";
import { CodeField } from "./ResinCodePicker";
import type { Lookups, ResinContext, Values } from "./types";

interface Props {
  def: FieldDef;
  values: Values;
  lookups: Lookups;
  resin: ResinContext;
  readOnly: boolean;
  onChange: (key: string, value: string) => void;
}

export function LogField({ def, values, lookups, resin, readOnly, onChange }: Props) {
  const id = `log-${def.key}`;
  const label = resolve(def.label, values) ?? def.key;
  const required = resolve(def.required, values) ?? false;
  const disabled = readOnly || (def.disabled?.(values) ?? false);
  const value = disabled && def.disabled?.(values) ? "" : (values[def.key] ?? "");
  const handle = (e: { target: { value: string } }) => onChange(def.key, e.target.value);

  if (def.kind === "codePick" && isCodeFieldKey(def.key)) {
    return (
      <div className={`f${def.span}`}>
        <CodeField
          id={id}
          fieldKey={def.key}
          resin={resin}
          picks={values}
          disabled={disabled}
          onChange={onChange}
        />
      </div>
    );
  }

  return (
    <div className={`f${def.span}`}>
      <label className="fl" htmlFor={id}>
        {label}
        {required && <span className="req">*</span>}
      </label>
      {def.kind === "textarea" ? (
        <textarea
          id={id}
          className="ctl"
          value={value}
          required={required}
          disabled={disabled}
          maxLength={def.maxLength}
          placeholder={def.placeholder}
          onChange={handle}
        />
      ) : def.kind === "select" ? (
        <select id={id} className="ctl" value={value} required={required} disabled={disabled} onChange={handle}>
          {def.emptyOption !== undefined && <option value="">{def.emptyOption}</option>}
          {(def.options?.(lookups) ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          type={def.kind}
          className="ctl"
          value={value}
          required={required}
          disabled={disabled}
          min={resolve(def.min, values)}
          max={resolve(def.max, values)}
          step={def.step}
          maxLength={def.maxLength}
          placeholder={def.placeholder}
          onChange={handle}
        />
      )}
    </div>
  );
}
